/**
 * ExecutiveSummary Component
 *
 * High-level executive summary dashboard showing ROI, cost breakdown,
 * and key business metrics from discovery analysis.
 *
 * Features:
 * - ROI metrics with gauge charts
 * - Cost breakdown visualization
 * - Risk mitigation metrics
 * - Migration timeline
 * - PDF export capability
 * - Professional executive-ready design
 */

import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import {
  TrendingUp, DollarSign, Calendar, Shield, Download, FileText,
  AlertTriangle, CheckCircle, Clock, Target, Zap
} from 'lucide-react'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import './ExecutiveSummary.css'

/**
 * Format currency
 */
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Format percentage
 */
function formatPercent(value) {
  return `${value.toLocaleString()}%`
}

/**
 * ROI Gauge Chart Component
 */
function ROIGauge({ value, max = 500, label, size = 140 }) {
  const svgRef = useRef(null)

  useEffect(() => {
    if (!svgRef.current) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const radius = size / 2 - 10
    const innerRadius = radius - 15

    const g = svg
      .attr('width', size)
      .attr('height', size * 0.7)
      .append('g')
      .attr('transform', `translate(${size / 2}, ${size * 0.65})`)

    // Background arc
    const backgroundArc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius)
      .startAngle(-Math.PI * 0.75)
      .endAngle(Math.PI * 0.75)

    g.append('path')
      .attr('d', backgroundArc)
      .attr('fill', '#24283b')

    // Value arc
    const scale = d3.scaleLinear()
      .domain([0, max])
      .range([0, Math.PI * 1.5])

    const clampedValue = Math.min(Math.max(value, 0), max)
    const valueAngle = scale(clampedValue)

    const valueArc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius)
      .startAngle(-Math.PI * 0.75)
      .endAngle(-Math.PI * 0.75 + valueAngle)

    // Color gradient based on value
    const color = value > 300 ? '#9ece6a' : value > 100 ? '#7dcfff' : '#e0af68'

    g.append('path')
      .attr('d', valueArc)
      .attr('fill', color)
      .transition()
      .duration(1500)
      .attrTween('d', function() {
        const interpolate = d3.interpolate(0, valueAngle)
        return function(t) {
          const currentAngle = interpolate(t)
          const arc = d3.arc()
            .innerRadius(innerRadius)
            .outerRadius(radius)
            .startAngle(-Math.PI * 0.75)
            .endAngle(-Math.PI * 0.75 + currentAngle)
          return arc()
        }
      })

    // Value text
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', -20)
      .attr('font-size', '28px')
      .attr('font-weight', '700')
      .attr('fill', '#c0caf5')
      .text(value.toLocaleString() + '%')

    // Label
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 5)
      .attr('font-size', '12px')
      .attr('fill', '#a9b1d6')
      .text(label)

  }, [value, max, label, size])

  return <svg ref={svgRef}></svg>
}

/**
 * Cost Breakdown Chart
 */
function CostBreakdownChart({ data, width = 400, height = 250 }) {
  const svgRef = useRef(null)

  useEffect(() => {
    if (!data || !svgRef.current) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const margin = { top: 20, right: 20, bottom: 60, left: 80 }
    const chartWidth = width - margin.left - margin.right
    const chartHeight = height - margin.top - margin.bottom

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const chartData = [
      { label: 'Infrastructure', current: data.infrastructure_costs?.current_mainframe_annual || 0, projected: data.infrastructure_costs?.projected_aws_annual || 0 },
      { label: 'Maintenance', current: data.maintenance_costs?.legacy_annual || 0, projected: data.maintenance_costs?.modern_annual || 0 },
      { label: 'Development', current: data.development_costs?.manual_rewrite_cost || 0, projected: data.development_costs?.ai_accelerated_cost || 0 },
    ]

    const categories = chartData.map(d => d.label)
    const x0 = d3.scaleBand()
      .domain(categories)
      .range([0, chartWidth])
      .padding(0.2)

    const x1 = d3.scaleBand()
      .domain(['Current', 'Projected'])
      .range([0, x0.bandwidth()])
      .padding(0.05)

    const y = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => Math.max(d.current, d.projected)) * 1.1])
      .range([chartHeight, 0])

    // Bars
    const groups = g.selectAll('.bar-group')
      .data(chartData)
      .enter()
      .append('g')
      .attr('transform', d => `translate(${x0(d.label)},0)`)

    groups.append('rect')
      .attr('x', x1('Current'))
      .attr('y', chartHeight)
      .attr('width', x1.bandwidth())
      .attr('height', 0)
      .attr('fill', '#f7768e')
      .attr('rx', 3)
      .transition()
      .duration(800)
      .attr('y', d => y(d.current))
      .attr('height', d => chartHeight - y(d.current))

    groups.append('rect')
      .attr('x', x1('Projected'))
      .attr('y', chartHeight)
      .attr('width', x1.bandwidth())
      .attr('height', 0)
      .attr('fill', '#9ece6a')
      .attr('rx', 3)
      .transition()
      .duration(800)
      .delay(200)
      .attr('y', d => y(d.projected))
      .attr('height', d => chartHeight - y(d.projected))

    // Axes
    g.append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(x0))
      .selectAll('text')
      .attr('fill', '#a9b1d6')

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => formatCurrency(d)))
      .selectAll('text')
      .attr('fill', '#a9b1d6')

  }, [data, width, height])

  return <svg ref={svgRef}></svg>
}

/**
 * Export to PDF
 */
async function exportToPDF(containerRef) {
  if (!containerRef.current) return

  try {
    const canvas = await html2canvas(containerRef.current, {
      backgroundColor: '#1a1b26',
      scale: 2,
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
    pdf.save('executive-summary.pdf')
  } catch (err) {
    console.error('PDF export failed:', err)
  }
}

/**
 * Main ExecutiveSummary component
 */
function ExecutiveSummary({ discoveryData, title = 'Executive Summary' }) {
  const containerRef = useRef(null)

  if (!discoveryData?.roi_analysis) {
    return (
      <div className="executive-summary-empty">
        <TrendingUp size={48} strokeWidth={1.5} />
        <p>No discovery data available</p>
      </div>
    )
  }

  const roi = discoveryData.roi_analysis
  const headline = roi.headline_metrics || {}
  const costs = roi.cost_breakdown || {}
  const risks = costs.risk_mitigation || {}

  const roiPercent = headline.five_year_roi_percent || 0
  const totalSavings = headline.five_year_savings || 0
  const paybackMonths = headline.payback_months || 0
  const annualSavings = headline.annual_recurring_savings || 0

  return (
    <div className="executive-summary" ref={containerRef}>
      {/* Header */}
      <div className="summary-header">
        <div className="summary-title">
          <h1><TrendingUp size={28} /> {title}</h1>
          <p className="summary-subtitle">Modernization Business Case Analysis</p>
        </div>
        <button
          onClick={() => exportToPDF(containerRef)}
          className="pdf-export-btn"
          title="Export to PDF"
        >
          <FileText size={18} />
          Export PDF
        </button>
      </div>

      {/* Hero Metrics */}
      <div className="hero-metrics">
        <div className="hero-card primary">
          <div className="hero-icon">
            <TrendingUp size={32} />
          </div>
          <div className="hero-content">
            <div className="hero-value">{formatPercent(roiPercent)}</div>
            <div className="hero-label">5-Year ROI</div>
            <div className="hero-badge excellent">Excellent</div>
          </div>
        </div>

        <div className="hero-card">
          <div className="hero-icon">
            <DollarSign size={32} />
          </div>
          <div className="hero-content">
            <div className="hero-value">{formatCurrency(totalSavings)}</div>
            <div className="hero-label">Total Savings (5 Years)</div>
            <div className="hero-detail">{formatCurrency(annualSavings)}/year</div>
          </div>
        </div>

        <div className="hero-card">
          <div className="hero-icon">
            <Clock size={32} />
          </div>
          <div className="hero-content">
            <div className="hero-value">{paybackMonths}</div>
            <div className="hero-label">Payback Period</div>
            <div className="hero-detail">months</div>
          </div>
        </div>
      </div>

      {/* ROI Gauges */}
      <div className="roi-section">
        <h3>ROI Analysis</h3>
        <div className="roi-gauges">
          <div className="gauge-card">
            <ROIGauge value={roiPercent} max={500} label="5-Year ROI %" />
          </div>
          <div className="gauge-card">
            <ROIGauge
              value={costs.development_costs?.savings_percent || 0}
              max={100}
              label="Dev Cost Savings"
              size={140}
            />
          </div>
          <div className="gauge-card">
            <ROIGauge
              value={costs.infrastructure_costs?.savings_percent || 0}
              max={100}
              label="Infrastructure Savings"
              size={140}
            />
          </div>
          <div className="gauge-card">
            <ROIGauge
              value={costs.maintenance_costs?.savings_percent || 0}
              max={100}
              label="Maintenance Savings"
              size={140}
            />
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="cost-section">
        <h3>Cost Comparison: Current vs. Modernized</h3>
        <div className="cost-chart">
          <CostBreakdownChart data={costs} width={800} height={300} />
        </div>
        <div className="cost-legend">
          <div className="legend-item">
            <span className="legend-box current"></span>
            <span>Current (Legacy)</span>
          </div>
          <div className="legend-item">
            <span className="legend-box projected"></span>
            <span>Projected (Modern)</span>
          </div>
        </div>
      </div>

      {/* Risk Mitigation */}
      <div className="risk-section">
        <h3><Shield size={20} /> Risk Mitigation</h3>
        <div className="risk-cards">
          <div className="risk-card">
            <div className="risk-icon">
              <AlertTriangle size={24} />
            </div>
            <div className="risk-content">
              <div className="risk-value">{formatCurrency(risks.skills_shortage_risk_annual || 0)}</div>
              <div className="risk-label">Skills Shortage Risk Avoided/Year</div>
              <p className="risk-detail">75% of organizations report COBOL skills shortage</p>
            </div>
          </div>

          <div className="risk-card">
            <div className="risk-icon">
              <CheckCircle size={24} />
            </div>
            <div className="risk-content">
              <div className="risk-value">{formatCurrency(risks.system_stability_risk_annual || 0)}</div>
              <div className="risk-label">System Stability Risk Avoided/Year</div>
              <p className="risk-detail">Modern architecture improves reliability and uptime</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="metrics-grid">
        <div className="metric-box">
          <Zap size={20} />
          <div className="metric-content">
            <div className="metric-value">
              {costs.development_costs?.time_savings_days || 0} days
            </div>
            <div className="metric-label">Time Savings</div>
          </div>
        </div>

        <div className="metric-box">
          <Target size={20} />
          <div className="metric-content">
            <div className="metric-value">
              {costs.development_costs?.ai_duration_days || 0} days
            </div>
            <div className="metric-label">AI-Accelerated Timeline</div>
          </div>
        </div>

        <div className="metric-box">
          <Calendar size={20} />
          <div className="metric-content">
            <div className="metric-value">
              {costs.development_costs?.manual_duration_days || 0} days
            </div>
            <div className="metric-label">Manual Rewrite Timeline</div>
          </div>
        </div>

        <div className="metric-box">
          <DollarSign size={20} />
          <div className="metric-content">
            <div className="metric-value">
              {formatCurrency(headline.net_present_value || 0)}
            </div>
            <div className="metric-label">Net Present Value</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="summary-footer">
        <p>Generated with Claude Code - RecodeIT Platform</p>
        <p className="timestamp">Report Date: {new Date().toLocaleDateString()}</p>
      </div>
    </div>
  )
}

export default ExecutiveSummary
